import { type ReactNode, useState } from 'react';
import {
  Box,
  Button as MantineButton,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconX } from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import type { Client } from 'src/admin/users/types';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { NonClientMeetingLink } from './NonClientMeetingLink';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import { useFileUpload } from 'src/data-capture/hooks/file-uploads/useFileUpload';
import { useFamilyFileDetections } from 'src/data-capture/hooks/file-uploads/useFamilyFileDetections';
import { useRegenerateDetections } from 'src/data-capture/hooks/file-uploads/useRegenerateDetections';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import { useBotStatus } from 'src/meetings/hooks/useBotStatus';
import { useEvent } from 'src/meetings/hooks/useEvent';
import { CalendarGuestChangesBanner } from 'src/meetings/detail/components/CalendarGuestChangesBanner';
import { MeetingOutOfDateBanner } from 'src/meetings/detail/components/MeetingOutOfDateBanner';
import { useForceBotJoin } from 'src/meetings/hooks/useForceBotJoin';
import { useCreateRecordingDataVersion } from 'src/meetings/hooks/useCreateRecordingDataVersion';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import {
  assignSpeakerPath,
  meetingDetailPath,
  meetingScopeFromParams,
  meetingsBasePath,
  type MeetingScope,
} from 'src/meetings/scope';
import { getUnidentifiedSpeakers } from 'src/meetings/utils/speaker-utils';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import {
  getAllClientParticipants,
  getClientParticipantIds,
  getParticipantDisplayNames,
} from 'src/meetings/utils/event-utils';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import { resolveMeetingOverviews } from './meetingOverviews';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  clientsAwaitingProfileUpdate,
  profileUpdateTargets,
  resolveRecordingFamilyId,
  sharedFamilyId,
} from './profileUpdateSuggestions';
import { MeetingPreparation } from './MeetingPreparation';
import { ParticipantPill } from './ParticipantPill';
import { PastMeetingTabs, type TabValue } from './PastMeetingTabs';
import { SuggestedActionCard } from './SuggestedActionCard';
import { SuggestedActions } from './SuggestedActions';

export default function MeetingDetailsScreen() {
  const {
    familyId,
    clientId,
    eventId = '',
    eventTab,
  } = useParams<{ familyId?: string; clientId?: string; eventId: string; eventTab?: string }>();
  const navigate = useNavigate();
  const scope: MeetingScope = meetingScopeFromParams({ familyId, clientId });
  const { event: meeting, isLoading, refetch } = useEvent(eventId);
  const { family } = useFamily(familyId);
  const clientFamilyMap = useClientFamilyMap();
  const { clients, isLoading: isClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { client: individualClient } = useClient(clientId);
  const { forceBotJoin, isLoading: isBotJoinLoading } = useForceBotJoin(eventId);
  const { botStatus, isLoading: isBotStatusLoading } = useBotStatus(eventId);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [pendingProfileUpdateId, setPendingProfileUpdateId] = useState<string | undefined>(
    undefined,
  );
  const [regeneration, setRegeneration] = useState<'idle' | 'requested' | 'running'>('idle');
  const { createClientDataVersion } = useCreateRecordingDataVersion(eventId);

  const meetingsPath = meetingsBasePath(scope);
  const isFamilyScope = scope.kind === 'family';
  const isHub = scope.kind === 'all';

  const clientParticipantIds = meeting ? getClientParticipantIds(meeting) : [];
  // Address and phone live on the full client's personalDetails, which the family
  // clients list omits, so fetch each participant's full record for those fields.
  const participantClientQueries = useQueries({
    queries: clientParticipantIds.map(id => ({ queryKey: ['clients', id], enabled: !!id })),
  });
  const fullClientById = Object.fromEntries(
    participantClientQueries
      .map(result => result.data as Client | undefined)
      .filter((client): client is Client => !!client)
      .map(client => [client.id, client]),
  );
  const areParticipantClientsLoading = participantClientQueries.some(result => result.isLoading);

  const clientParticipantCount = meeting ? getAllClientParticipants(meeting).length : 0;
  const { flagEnabledMeetingsRework } = useFlags();
  // Outside the family route nothing in the URL names the meeting's family, so read it from the
  // attendees' own client records — not from the adviser's families list, which for an ADVISOR
  // holds only their own households and so would treat a colleague's as unrelated clients.
  const participantFamilyIds = clientParticipantIds.map(id => fullClientById[id]?.familyId);
  // A meeting spanning families (or none) is reviewed per client, through each one's own copy of
  // the recording. Without that copy there is nowhere for the clients outside the lead attendee's
  // family to go, so until it is on, keep reading the family from the lead attendee as before.
  const participantFamilyId = flagEnabledMeetingsRework
    ? sharedFamilyId(participantFamilyIds)
    : participantFamilyIds[0];
  const recordingFamilyId = resolveRecordingFamilyId(
    scope,
    clientParticipantCount,
    scope.kind === 'family' ? undefined : participantFamilyId,
    flagEnabledMeetingsRework,
  );

  // A finished meeting with a ready transcript can have a meeting note generated.
  const isPast = meeting ? isPastEvent(meeting) : false;
  const { transcript } = useTranscript({
    eventId,
    enabled: isPast && !!meeting?.hasTranscription,
  });
  const transcriptReady = transcript?.status === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(eventId, {
    enabled: isPast && transcriptReady,
  });
  const noteReady = meetingNote?.status === MeetingNoteStatus.READY;
  const noteExistsOrGenerating =
    noteReady || meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;
  // Only offer to generate a note when the transcript is ready and there isn't one already.
  const canCreateNote = isPast && transcriptReady && !noteExistsOrGenerating;

  const dataUploadFileId = transcript?.dataUploadFileId;
  const recordingOwnerClientId = transcript?.recordingOwnerClientId ?? undefined;
  const { file: recordingFile } = useFileUpload(dataUploadFileId, {
    clientId: recordingOwnerClientId,
    familyId: recordingFamilyId,
    enabled: isPast && transcriptReady && (!!recordingOwnerClientId || !!recordingFamilyId),
    autoRefreshProcessing: true,
  });
  // Detection rows land as the file is processed and a recording may yield none, so
  // poll the per-member detections only while that processing is in flight — the file's
  // background process is the authoritative "done" signal, not the presence of rows.
  const detectionStatus = recordingFile?.backgroundProcess?.status;
  const isDetectionProcessing = detectionStatus === 'PENDING' || detectionStatus === 'PROCESSING';
  const { members: familyDetectionMembers } = useFamilyFileDetections(
    recordingFamilyId,
    dataUploadFileId,
    { enabled: isPast && transcriptReady, autoRefresh: isDetectionProcessing },
  );
  const { regenerateDetections, isRegenerating } = useRegenerateDetections(dataUploadFileId, {
    clientId: recordingOwnerClientId,
    familyId: recordingFamilyId,
  });
  // A reassigned speaker can leave suggestions with the wrong person, so none are offered until
  // they are regenerated, nor while that run replaces them.
  const detectionsOutOfDate = flagEnabledMeetingsRework && !!recordingFile?.detectionsOutOfDate;
  const awaitingRegeneratedSuggestions =
    flagEnabledMeetingsRework && (detectionsOutOfDate || isDetectionProcessing);
  // The regeneration is over once the detection it started has run, so a later detection (a
  // recording a client claims, say) is not shown as this one.
  const regenerationRunning = isRegenerating || isDetectionProcessing;
  if (regeneration === 'requested' && regenerationRunning) setRegeneration('running');
  if (regeneration === 'running' && !regenerationRunning) setRegeneration('idle');

  // The scope's client list backs the ownership guard below, so wait for it
  // before deciding whether a family meeting belongs to this family.
  if ((isLoading && !meeting) || (isFamilyScope && isClientsLoading)) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (!meeting) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const meetingUrl = meeting.meetingUrl;
  const enrichedClients = individualClient ? [...clients, individualClient] : clients;
  const clientById = Object.fromEntries(enrichedClients.map(client => [client.id, client]));

  const clientParticipants = getAllClientParticipants(meeting)
    .map(p => p.participant.client)
    .filter((client): client is NonNullable<typeof client> => client !== null);

  // useEvent fetches any event by id, and the backend only isolates by tenant,
  // so a scoped route must confirm the meeting actually belongs to that family
  // or client before rendering it inside their shell.
  const belongsToScope =
    scope.kind === 'all' ||
    (scope.kind === 'individual' &&
      clientParticipants.some(participant => participant.id === scope.clientId)) ||
    (scope.kind === 'family' &&
      clientParticipants.some(participant => clients.some(client => client.id === participant.id)));

  if (!belongsToScope) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const participantNames = getParticipantDisplayNames(meeting, clientFamilyMap);

  const participantInfo = clientParticipants.map(simple => {
    const full = clientById[simple.id];
    const fullDetails = fullClientById[simple.id];
    return {
      id: simple.id,
      firstName: simple.firstName,
      name: `${simple.firstName} ${simple.lastName}`.trim(),
      address: fullDetails?.personalDetails.address ?? null,
      email: full?.emailAddress ?? simple.emailAddress ?? null,
      phone: fullDetails?.personalDetails.phoneNumber ?? null,
    };
  });

  // "Update profile" suggestions come from the recording's entity detections, not
  // from the meeting note, so they surface as soon as detection has run. Offer a
  // card only for a client who actually has detections still awaiting review.
  // Which family a client belongs to decides whether they are reviewed through the family or
  // through their own copy of the recording, so wait for their records before offering any card.
  const unresolvedParticipants =
    areParticipantClientsLoading || awaitingRegeneratedSuggestions
      ? []
      : profileUpdateTargets(
          clientsAwaitingProfileUpdate({
            recordingFamilyId,
            recordingFile,
            familyDetectionMembers,
            canReviewEveryDetectedClient: flagEnabledMeetingsRework,
          }),
          participantInfo,
          [
            ...(flagEnabledMeetingsRework
              ? familyDetectionMembers.map(member => member.client)
              : []),
            ...(recordingFile?.detectedClients ?? []),
          ],
        );

  const unidentifiedSpeakers = transcriptReady
    ? getUnidentifiedSpeakers(transcript?.segments, transcript?.speakerFragments)
    : [];

  // Show a single family overview when every client attendee belongs to the same
  // family (the family route always resolves to its own family); otherwise each
  // attendee gets their own individual overview, so unrelated clients are never
  // grouped under one family overview.
  const routeFamily = isFamilyScope && family ? { id: family.id, name: family.name } : null;
  const overviews = resolveMeetingOverviews(
    participantInfo.map(participant => ({ id: participant.id, name: participant.name })),
    clientFamilyMap,
    routeFamily,
  );

  // On the hub a meeting can have no Afternoon client attached at all. There is
  // no client to prepare for or generate notes from, so those meetings get the
  // link-a-client panel instead of the standard prep/past body.
  const isClientlessHub = isHub && clientParticipants.length === 0;

  const openMeeting = () => {
    if (meetingUrl) window.open(meetingUrl, '_blank');
  };

  const handleForceBotJoin = async () => {
    try {
      await forceBotJoin();
      notifications.show({
        title: 'Bot joining meeting',
        message: 'The meeting bot will join within 10 seconds',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to schedule bot',
        message: 'Could not reschedule the bot to join now. Please try again.',
        color: 'red',
      });
    }
  };

  const handleJoinMeetingClick = () => {
    const minutesFromStart =
      (Date.now() - new Date(meeting.scheduledStartAt).getTime()) / 1000 / 60;

    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CLICKED, {
      botStatus: botStatus || 'UNKNOWN',
      minutesFromStart: Math.abs(minutesFromStart),
      meetingUrl,
    });

    if (
      botStatus === 'ACTIVE' ||
      botStatus === 'NONE' ||
      botStatus === 'FINISHED' ||
      Math.abs(minutesFromStart) <= 2
    ) {
      openMeeting();
      return;
    }

    setRescheduleOpen(true);
  };

  const handleConfirmJoin = async (rescheduleBot: boolean) => {
    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CONFIRMED, { rescheduleBot, meetingUrl });
    if (rescheduleBot) await handleForceBotJoin();
    setRescheduleOpen(false);
    openMeeting();
  };

  const handleAddBotClick = async () => {
    Mixpanel.track(MixpanelEvents.BOT_MANUALLY_ADDED_TO_MEETING);
    await handleForceBotJoin();
  };

  const handleReviewDetections = async (participantClientId: string) => {
    // Whether a review is done is recorded on the file, so two clients outside a family cannot
    // share one: anyone who doesn't own the recording reviews their own copy of it instead.
    const needsOwnCopy = !recordingFamilyId && participantClientId !== recordingOwnerClientId;
    setPendingProfileUpdateId(participantClientId);
    try {
      const fileId = needsOwnCopy
        ? await createClientDataVersion(participantClientId)
        : (transcript?.dataUploadFileId ?? '');
      const params = new URLSearchParams({
        fileId,
        fileName: meeting.title,
        guid: uuidv4(),
        originMeetingId: meeting.id,
      });
      if (recordingFamilyId) params.set('familyId', recordingFamilyId);
      if (scope.kind === 'family') params.set('originFamilyId', scope.familyId);
      if (scope.kind === 'individual') params.set('originClientId', scope.clientId);
      navigate(`/client/${participantClientId}/detected-entities?${params.toString()}`);
    } catch (error) {
      notifyAndLogError('Failed to prepare data for review', 'Please try again later', error, {
        eventId: meeting.id,
        clientId: participantClientId,
      });
      setPendingProfileUpdateId(undefined);
    }
  };

  const handleRegenerateSuggestions = async () => {
    setRegeneration('requested');
    try {
      await regenerateDetections();
    } catch (error) {
      notifyAndLogError('Failed to regenerate suggestions', 'Please try again later', error, {
        eventId: meeting.id,
        fileId: dataUploadFileId,
      });
      setRegeneration('idle');
    }
  };

  const dateTimePill = (
    <Flex align="center" p="xxs" bg="gray.1" style={{ borderRadius: 12, width: 'fit-content' }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSchedule(meeting)}
      </Text>
    </Flex>
  );

  // The tab lives in the URL so the meetings list and the suggested actions can
  // both send the adviser straight to a tab.
  const openTab = (tab: TabValue) =>
    navigate(meetingDetailPath(scope, meeting.id, tab), { replace: true });

  // Unidentified speakers are identified before anything else, because the note and the
  // profile updates both attribute what was said to whoever said it. Once every speaker is
  // named, generating the note and reviewing member profiles are offered together.
  const firstUnidentifiedSpeaker = unidentifiedSpeakers[0];
  const suggestedActionCards: ReactNode[] = [];
  if (firstUnidentifiedSpeaker) {
    suggestedActionCards.push(
      <SuggestedActionCard
        key="identify-speakers"
        header="Identify"
        name="Speakers"
        onClick={() =>
          navigate(assignSpeakerPath(scope, meeting.id, firstUnidentifiedSpeaker.label))
        }
      />,
    );
  } else {
    if (regeneration !== 'idle' && regenerationRunning) {
      suggestedActionCards.push(
        <SuggestedActionCard
          key="regenerate-suggestions"
          header="Regenerating"
          name="Profile updates"
          isLoading
        />,
      );
    }
    // Reprocessing the meeting is the one thing to do while its suggestions are out of date or
    // being replaced; the note is offered again once that run has landed.
    if (canCreateNote && !awaitingRegeneratedSuggestions) {
      suggestedActionCards.push(
        <SuggestedActionCard
          key="generate-note"
          header="Generate"
          name="Meeting note"
          onClick={() => openTab('meeting-note')}
        />,
      );
    }
    suggestedActionCards.push(
      ...unresolvedParticipants.map(participant => (
        <SuggestedActionCard
          key={`update:${participant.id}`}
          header="Update"
          name={participant.name}
          isLoading={pendingProfileUpdateId === participant.id}
          onClick={
            pendingProfileUpdateId ? undefined : () => void handleReviewDetections(participant.id)
          }
        />
      )),
    );
  }
  const suggestedActions: ReactNode =
    suggestedActionCards.length > 0 ? (
      <SuggestedActions>{suggestedActionCards}</SuggestedActions>
    ) : null;

  const handleClientLinked = async () => {
    await refetch();
  };

  // The same preparation view is shown before a meeting and under a past
  // meeting's pre-meeting tab, so build it once and reuse it in both places. It
  // also carries the unrecognised-attendees panel at the bottom of its Stack.
  const meetingPreparation = (
    <MeetingPreparation
      overviews={overviews}
      participantInfo={participantInfo}
      scope={scope}
      meeting={meeting}
      onAttendeeLinked={handleClientLinked}
    />
  );

  let pastBody: ReactNode;
  if (isClientlessHub) {
    // A recorded meeting can still gain a client; one that was never recorded
    // has nothing to attach, so it keeps the old terminal message.
    pastBody = meeting.hasTranscription ? (
      <NonClientMeetingLink
        event={meeting}
        scope={scope}
        onLinked={handleClientLinked}
        context="recorded"
      />
    ) : (
      <Box mt="xl">
        <Text c="gray.6">
          This meeting does not have transcription, summary, or notes because it was not linked to a
          client.
        </Text>
      </Box>
    );
  } else {
    pastBody = (
      <>
        {suggestedActions && <Box mt="sm">{suggestedActions}</Box>}
        <Box mt="lg">
          <PastMeetingTabs
            meeting={meeting}
            transcript={transcript}
            activeTab={eventTab}
            onTabChange={openTab}
            scope={scope}
            preMeetingContent={meetingPreparation}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        {isPast ? (
          <Flex align="center" justify="space-between" gap="xxs">
            <Flex align="center" gap="xxs">
              <IconButton
                size={36}
                bg="gray.1"
                c="gray.9"
                aria-label="Back to meetings"
                onClick={() => navigate(meetingsPath)}
                style={{ borderRadius: 12 }}
              >
                <IconArrowLeft size={20} />
              </IconButton>
              {dateTimePill}
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
                px="xs"
                py="xxs"
                gap={8}
              />
            </Flex>
          </Flex>
        ) : (
          <Flex justify="space-between" align="flex-start" gap="sm">
            {dateTimePill}
            <Flex align="center" gap="xxs">
              <IconButton
                size={40}
                bg="gray.1"
                c="gray.9"
                aria-label="Close"
                onClick={() => navigate(meetingsPath)}
              >
                <IconX size={20} />
              </IconButton>
            </Flex>
          </Flex>
        )}

        <Flex align="center" mt="xs" py={5}>
          {meeting.meetingPlatform && (
            <Box style={{ paddingRight: 12 }}>
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={28} />
            </Box>
          )}
          <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
        </Flex>

        <CalendarGuestChangesBanner event={meeting} scope={scope} mt="lg" />

        {isPast && !isClientlessHub && meeting.hasTranscription && (
          <Box mt="lg">
            <MeetingOutOfDateBanner
              event={meeting}
              profileUpdates={
                detectionsOutOfDate
                  ? { regenerate: handleRegenerateSuggestions, isRegenerating }
                  : undefined
              }
            />
          </Box>
        )}

        {isPast ? (
          pastBody
        ) : (
          <>
            <Flex justify="space-between" align="center" mt="sm" gap="sm">
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
              />
              <Group gap="sm" wrap="nowrap">
                <Button
                  variant="primary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  disabled={!meetingUrl || isBotJoinLoading}
                  onClick={handleJoinMeetingClick}
                >
                  Join meeting
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  loading={isBotJoinLoading || isBotStatusLoading}
                  disabled={botStatus === 'ACTIVE' || !meetingUrl}
                  onClick={handleAddBotClick}
                >
                  {botStatus === 'ACTIVE' ? 'Bot recording' : 'Add bot'}
                </Button>
              </Group>
            </Flex>

            <Divider mt="md" color="gray.1" />

            {isClientlessHub ? (
              <NonClientMeetingLink
                event={meeting}
                scope={scope}
                onLinked={handleClientLinked}
                context="upcoming"
              />
            ) : (
              <>
                <Text mt="lg" size="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  Meeting preparation
                </Text>

                <Box mt="sm">{meetingPreparation}</Box>
              </>
            )}
          </>
        )}
      </Box>

      <AddExpectedAttendeesModal
        opened={attendeesOpen}
        onClose={() => setAttendeesOpen(false)}
        scope={scope}
        meeting={meeting}
      />

      <Modal
        opened={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        title="Reschedule meeting bot?"
        centered
      >
        <Text size="textMd" mb="xs">
          Do you want to reschedule the bot to join the meeting immediately?
        </Text>
        <Text size="textSm" c="gray.6" mb="lg">
          If the meeting is starting early or you want the bot to join now, select &quot;Yes&quot;.
          Otherwise, the bot will join at the scheduled time.
        </Text>
        <Group justify="flex-end" gap="sm">
          <MantineButton
            variant="subtle"
            onClick={() => handleConfirmJoin(false)}
            disabled={isBotJoinLoading}
          >
            No, just join
          </MantineButton>
          <MantineButton
            variant="filled"
            onClick={() => handleConfirmJoin(true)}
            loading={isBotJoinLoading}
          >
            Yes, reschedule bot
          </MantineButton>
        </Group>
      </Modal>
    </>
  );
}

function formatSchedule(meeting: FamilyMeeting): string {
  const startIso = meeting.startAt ?? meeting.scheduledStartAt;
  const endIso = meeting.endAt ?? meeting.scheduledEndAt;
  const start = new Date(startIso);
  const end = new Date(endIso);

  const date = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  const startMeridiem = start.getHours() >= 12 ? 'PM' : 'AM';
  const endMeridiem = end.getHours() >= 12 ? 'PM' : 'AM';
  const startText = formatClock(start, startMeridiem !== endMeridiem);
  const endText = formatClock(end, true);

  return `${date}, ${startText}-${endText} (${formatMeetingDuration(startIso, endIso)})`;
}

function formatClock(date: Date, withMeridiem: boolean): string {
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = date.getHours() >= 12 ? 'PM' : 'AM';
  return withMeridiem ? `${hours}:${minutes}${meridiem}` : `${hours}:${minutes}`;
}
